import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import type { CheckCounts, Counters, EventNotice, FilesStatus, Id, PersonRef, PresenceEntry, SpaceRef, Ts, WsLock } from "../api/types";

export type LiveStatus = "idle" | "connecting" | "open" | "reconnecting" | "closed";

export interface LockInfo {
  element_id: Id;
  holder: PersonRef;
  expires_at?: Ts | null;
  release_asked_by?: PersonRef | null;
}

export interface LiveCounts {
  checks: CheckCounts;
  counters: Counters;
}

/** How many notices the store keeps for the small live feeds. */
export const RECENT_LIMIT = 50;

interface LiveData {
  status: LiveStatus;
  /** The number of the last notice that was applied, in order. */
  head: number;
  /** The newest head the server has announced. A client that sees `serverHead > head` is behind. */
  serverHead: number;
  space: SpaceRef | null;
  workspaceCode: string | null;
  membershipId: Id | null;
  presence: PresenceEntry[];
  /** Counts up with every presence list the socket pushes, so a read that was slower than the push is dropped. */
  presenceVersion: number;
  files: FilesStatus | null;
  counts: LiveCounts | null;
  locks: Record<Id, LockInfo>;
  /** The latest notices, newest first. */
  recent: EventNotice[];
  /** The last sentence from the server about a bad message (`error`), for a debug line. */
  lastError: string | null;
}

interface LiveActions {
  reset(): void;
  setStatus(status: LiveStatus): void;
  setHello(info: { head: number; space: SpaceRef; workspace_code: string; membership_id: Id }): void;
  setHead(head: number): void;
  setServerHead(head: number): void;
  setPresence(entries: PresenceEntry[]): void;
  /** The list read over REST when the socket joined: used only if the socket has pushed none since `version`. */
  seedPresence(entries: PresenceEntry[], version: number): void;
  setFiles(status: FilesStatus): void;
  setCounts(counts: LiveCounts): void;
  setLock(message: WsLock): void;
  pushNotice(notice: EventNotice): void;
  setError(text: string | null): void;
}

const initial: LiveData = {
  status: "idle",
  head: 0,
  serverHead: 0,
  space: null,
  workspaceCode: null,
  membershipId: null,
  presence: [],
  presenceVersion: 0,
  files: null,
  counts: null,
  locks: {},
  recent: [],
  lastError: null,
};

export const useLiveStore = create<LiveData & LiveActions>((set) => ({
  ...initial,
  reset: () => set({ ...initial }),
  setStatus: (status) => set({ status }),
  setHello: (info) =>
    set({ head: info.head, serverHead: info.head, space: info.space, workspaceCode: info.workspace_code, membershipId: info.membership_id }),
  setHead: (head) => set((state) => ({ head, serverHead: Math.max(state.serverHead, head) })),
  setServerHead: (serverHead) => set((state) => ({ serverHead: Math.max(state.serverHead, serverHead) })),
  setPresence: (presence) => set((state) => ({ presence, presenceVersion: state.presenceVersion + 1 })),
  seedPresence: (presence, version) => set((state) => (state.presenceVersion === version ? { presence } : state)),
  setFiles: (files) => set({ files }),
  setCounts: (counts) => set({ counts }),
  setLock: (message) =>
    set((state) => {
      const locks = { ...state.locks };
      if (message.holder) {
        locks[message.element_id] = {
          element_id: message.element_id,
          holder: message.holder,
          expires_at: message.expires_at ?? null,
          release_asked_by: message.release_asked_by ?? null,
        };
      } else {
        delete locks[message.element_id];
      }
      return { locks };
    }),
  pushNotice: (notice) => set((state) => ({ recent: [notice, ...state.recent].slice(0, RECENT_LIMIT) })),
  setError: (lastError) => set({ lastError }),
}));

/**
 * What the live channel knows, for the screens:
 * `head` (the last event applied), `presence` (who is where), `files` (the status of the written
 * files), `counts` (the checks and the counters), `locks` (who holds which element) and `recent`
 * (the latest notices, newest first). `connected` is true while the socket is open.
 */
export function useLive() {
  return useLiveStore(
    useShallow((state) => ({
      status: state.status,
      connected: state.status === "open",
      head: state.head,
      serverHead: state.serverHead,
      presence: state.presence,
      files: state.files,
      counts: state.counts,
      locks: state.locks,
      recent: state.recent,
    })),
  );
}

/** The lock on one element, as far as the live channel has told. */
export function useLock(elementId: Id | null | undefined): LockInfo | undefined {
  return useLiveStore((state) => (elementId ? state.locks[elementId] : undefined));
}
